/**
 * Imagem de produto com srcset, blur-up e zero CLS.
 *
 * Melhorias sobre o protótipo (que tinha só `<img src>` de Unsplash):
 *  - `srcset`/`sizes` sobre as 3 variantes que o backend gera, para o celular
 *    não baixar a versão de 1600px;
 *  - `width`/`height` explícitos + wrapper com `aspect-*`: o espaço é reservado
 *    antes da imagem chegar, então a página não pula (CLS zero);
 *  - `placeholder` (LQIP data URI) como background durante o carregamento;
 *  - `loading="lazy"` por padrão, `eager` + `fetchPriority` só onde importa.
 */
import { useEffect, useRef, useState } from 'react'
import type { ImagemProduto } from '@peniel/shared'
import { construirSrcSet, urlImagem } from '@peniel/shared'

import { cn } from '../lib/cn.js'
import { Icone } from './Icone.jsx'

interface PropsImagemResponsiva {
  imagem: ImagemProduto | null | undefined
  /** Descrição da foto. Use o nome da bike, nunca "imagem 1". */
  alt: string
  sizes: string
  /** Proporção do wrapper. O protótipo usava alturas fixas (h-64 / h-48). */
  className?: string
  classNameImg?: string
  /** Acima da dobra: carrega imediato e com prioridade alta. */
  prioridade?: boolean
  /** Zoom suave no hover do card pai (`group`). */
  zoomNoHover?: boolean
  /** Estado "vendida": apaga e dessatura a foto, como no protótipo. */
  esmaecida?: boolean
}

export function ImagemResponsiva({
  imagem,
  alt,
  sizes,
  className,
  classNameImg,
  prioridade = false,
  zoomNoHover = false,
  esmaecida = false,
}: PropsImagemResponsiva) {
  const [carregada, definirCarregada] = useState(false)
  const refImg = useRef<HTMLImageElement>(null)

  /**
   * Imagem que já está em cache pode terminar de carregar ANTES do React
   * anexar o `onLoad` — nesse caso o evento nunca dispara e, sem esta
   * checagem, a foto ficaria em `opacity-0` para sempre. `complete` cobre isso.
   */
  useEffect(() => {
    if (refImg.current?.complete) definirCarregada(true)
  }, [imagem?.arquivoBase])

  if (!imagem) {
    return (
      <div
        className={cn(
          'flex items-center justify-center bg-gray-100 text-gray-300',
          className,
        )}
      >
        <Icone nome="bike" className="text-4xl" titulo="Sem foto" />
      </div>
    )
  }

  return (
    <div
      className={cn('overflow-hidden bg-gray-200', className)}
      // O LQIP entra como background: aparece na hora e a foto real cobre
      // por cima, sem um segundo request.
      style={
        imagem.placeholder && !carregada
          ? {
              backgroundImage: `url("${imagem.placeholder}")`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }
          : undefined
      }
    >
      <img
        ref={refImg}
        src={urlImagem(imagem.arquivoBase, 'c')}
        srcSet={construirSrcSet(imagem.arquivoBase)}
        sizes={sizes}
        alt={alt}
        width={imagem.largura}
        height={imagem.altura}
        loading={prioridade ? 'eager' : 'lazy'}
        decoding="async"
        {...(prioridade ? { fetchPriority: 'high' as const } : {})}
        onLoad={() => definirCarregada(true)}
        // Se a imagem falhar, revela de qualquer jeito: o alt aparece, o que é
        // melhor que um retângulo vazio sem explicação.
        onError={() => definirCarregada(true)}
        className={cn(
          'size-full object-cover transition-opacity duration-300',
          // `motion-reduce:transform-none` para o zoom não incomodar quem
          // pediu menos movimento no sistema.
          zoomNoHover &&
            'transition-transform duration-300 ease-out group-hover:scale-105 motion-reduce:transform-none',
          esmaecida && 'grayscale',
          // UMA classe de opacidade só: `esmaecida` e o fade disputariam a
          // mesma propriedade, e o tailwind-merge deixaria a última ganhar.
          !carregada ? 'opacity-0' : esmaecida ? 'opacity-50' : 'opacity-100',
          classNameImg,
        )}
      />
    </div>
  )
}
